export type V1TopologyDeviceClass =
  | 'switch'
  | 'router'
  | 'firewall'
  | 'gateway'
  | 'wan_edge'
  | 'wireless'
  | 'wireless-controller'
  | 'load_balancer'
  | 'vpn'
  | 'server'
  | 'storage'
  | 'cloud'
  | 'endpoint'
  | 'generic';

export interface V1TopologyDevice {
  id: string;
  label: string;
  presentation: V1TopologyClassification;
}

export interface V1TopologyClassification {
  deviceClass: V1TopologyDeviceClass;
  tier: string;
  tierRank: number;
}

export interface V1TopologyLayoutResult {
  classifications: Map<string, V1TopologyClassification>;
  levels: Map<string, number>;
  positions: Map<string, { x: number; y: number }>;
  nodeWidth: number;
  nodeHeight: number;
  horizontalGap: number;
  verticalGap: number;
}

interface V1TopologyEdgePresentation {
  classes: string;
  routeOffset: number;
}

const presentationFor = (device: V1TopologyDevice): V1TopologyClassification => device.presentation;

const connectedComponents = (devices: V1TopologyDevice[], adjacency: Map<string, Set<string>>): string[][] => {
  const deviceIds = new Set(devices.map((device) => device.id));
  const visited = new Set<string>();
  const components: string[][] = [];
  [...deviceIds].sort().forEach((start) => {
    if (visited.has(start)) return;
    const component: string[] = [];
    const queue = [start];
    visited.add(start);
    for (let index = 0; index < queue.length; index += 1) {
      const id = queue[index];
      component.push(id);
      Array.from(adjacency.get(id) ?? [])
        .filter((neighbor) => deviceIds.has(neighbor))
        .sort()
        .forEach((neighbor) => {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        });
    }
    components.push(component);
  });
  return components.sort((a, b) => a[0].localeCompare(b[0]));
};

const computeDynamicLevels = (
  devices: V1TopologyDevice[],
  adjacency: Map<string, Set<string>>,
  classifications: Map<string, V1TopologyClassification>
): Map<string, number> => {
  const devicesByTier = new Map<number, V1TopologyDevice[]>();
  devices.forEach((device) => {
    const rank = classifications.get(device.id)?.tierRank ?? 0;
    devicesByTier.set(rank, [...(devicesByTier.get(rank) ?? []), device]);
  });

  const levels = new Map<string, number>();
  let levelStart = 0;
  [...devicesByTier.keys()].sort((a, b) => a - b).forEach((rank) => {
    const tierDevices = devicesByTier.get(rank) ?? [];
    const tierIds = new Set(tierDevices.map((device) => device.id));
    const depthById = new Map<string, number>();
    const components = connectedComponents(
      tierDevices,
      new Map(tierDevices.map((device) => [
        device.id,
        new Set(Array.from(adjacency.get(device.id) ?? []).filter((neighbor) => tierIds.has(neighbor)))
      ]))
    );

    components.forEach((component) => {
      const hasUpstreamConnection = (id: string) =>
        Array.from(adjacency.get(id) ?? []).some((neighbor) => {
          const neighborRank = classifications.get(neighbor)?.tierRank;
          return neighborRank !== undefined && neighborRank < rank;
        });
      let roots = component.filter(hasUpstreamConnection).sort();

      // A backend tier is semantic, not an instruction to flatten every device
      // in that tier. When a same-tier component has no connection to an upper
      // tier, use only its physical links to find an outward-to-internal order:
      // a device with fewer links to lower tiers is placed above its peer that
      // carries traffic farther into the site. Equal peers stay on one row.
      if (roots.length === 0) {
        const downstreamCounts = new Map(component.map((id) => [
          id,
          Array.from(adjacency.get(id) ?? []).filter((neighbor) => {
            const neighborRank = classifications.get(neighbor)?.tierRank;
            return neighborRank !== undefined && neighborRank > rank;
          }).length
        ]));
        const counts = Array.from(downstreamCounts.values());
        const minDownstreamCount = Math.min(...counts);
        const maxDownstreamCount = Math.max(...counts);
        // Different positive fan-out counts are common for redundant peers.
        // Only split a group when a true outer leaf (zero lower-tier links)
        // leads into a device that continues deeper into the topology.
        if (minDownstreamCount === 0 && maxDownstreamCount > 0) {
          roots = component.filter((id) => downstreamCounts.get(id) === minDownstreamCount).sort();
        }
      }

      // Multiple devices connected to the same upper part of the network are peers,
      // so they start together. When physical links give no directional signal, we
      // keep the whole group together rather than inventing a parent/child relationship.
      if (roots.length === 0) {
        component.forEach((id) => depthById.set(id, 0));
        return;
      }
      const queue = [...roots];
      roots.forEach((root) => depthById.set(root, 0));
      for (let index = 0; index < queue.length; index += 1) {
        const current = queue[index];
        const nextDepth = (depthById.get(current) ?? 0) + 1;
        Array.from(adjacency.get(current) ?? [])
          .filter((neighbor) => tierIds.has(neighbor))
          .sort()
          .forEach((neighbor) => {
            if (!depthById.has(neighbor)) {
              depthById.set(neighbor, nextDepth);
              queue.push(neighbor);
            }
          });
      }
    });

    const maxDepth = Math.max(0, ...tierDevices.map((device) => depthById.get(device.id) ?? 0));
    tierDevices.forEach((device) => levels.set(device.id, levelStart + (depthById.get(device.id) ?? 0)));
    levelStart += maxDepth + 1;
  });
  return levels;
};

const median = (values: number[]): number => {
  if (values.length === 0) return Number.POSITIVE_INFINITY;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

const reorderLayer = (
  layer: V1TopologyDevice[],
  referenceOrders: Map<string, number>,
  adjacency: Map<string, Set<string>>
): V1TopologyDevice[] =>
  [...layer].sort((a, b) => {
    const aScores = Array.from(adjacency.get(a.id) ?? []).map((id) => referenceOrders.get(id)).filter((v): v is number => v !== undefined);
    const bScores = Array.from(adjacency.get(b.id) ?? []).map((id) => referenceOrders.get(id)).filter((v): v is number => v !== undefined);
    const aMedian = median(aScores);
    const bMedian = median(bScores);
    const difference = aMedian - bMedian;
    if (Number.isFinite(difference) && difference !== 0) return difference;
    if (Number.isFinite(aMedian) !== Number.isFinite(bMedian)) return Number.isFinite(aMedian) ? -1 : 1;
    return 0;
  });

const orderComponentLayers = (
  componentDevices: V1TopologyDevice[],
  adjacency: Map<string, Set<string>>,
  levels: Map<string, number>
): Array<{ rank: number; devices: V1TopologyDevice[] }> => {
  const layers = new Map<number, V1TopologyDevice[]>();
  componentDevices.forEach((device) => {
    const rank = levels.get(device.id) ?? 0;
    layers.set(rank, [...(layers.get(rank) ?? []), device]);
  });
  const orderedRanks = [...layers.keys()].sort((a, b) => a - b);
  const ordered = new Map<number, V1TopologyDevice[]>();
  orderedRanks.forEach((rank) => ordered.set(rank, connectivityWalkOrder(layers.get(rank) ?? [], adjacency)));

  for (let pass = 0; pass < 6; pass += 1) {
    for (let index = 1; index < orderedRanks.length; index += 1) {
      const reference = new Map<string, number>();
      for (let previous = 0; previous < index; previous += 1) {
        (ordered.get(orderedRanks[previous]) ?? []).forEach((device, position) => reference.set(device.id, position));
      }
      const rank = orderedRanks[index];
      ordered.set(rank, reorderLayer(ordered.get(rank) ?? [], reference, adjacency));
    }
    for (let index = orderedRanks.length - 2; index >= 0; index -= 1) {
      const reference = new Map<string, number>();
      for (let next = index + 1; next < orderedRanks.length; next += 1) {
        (ordered.get(orderedRanks[next]) ?? []).forEach((device, position) => reference.set(device.id, position));
      }
      const rank = orderedRanks[index];
      ordered.set(rank, reorderLayer(ordered.get(rank) ?? [], reference, adjacency));
    }
  }

  return orderedRanks.map((rank) => ({ rank, devices: ordered.get(rank) ?? [] }));
};

const topologySizing = (deviceCount: number, edgeCount: number) => {
  const density = deviceCount > 0 ? (edgeCount * 2) / deviceCount : 0;
  const nodeWidth = deviceCount <= 16 ? 164 : deviceCount <= 40 ? 148 : 136;
  const nodeHeight = deviceCount <= 16 ? 94 : deviceCount <= 40 ? 86 : 80;
  const horizontalGap = nodeWidth + 58 + Math.min(54, density * 8);
  const verticalGap = nodeHeight + 126 + Math.min(80, density * 12);
  return { nodeWidth, nodeHeight, horizontalGap, verticalGap };
};

const edgeCountFor = (devices: V1TopologyDevice[], adjacency: Map<string, Set<string>>): number =>
  Math.round(devices.reduce((sum, device) => sum + (adjacency.get(device.id)?.size ?? 0), 0) / 2);

const splitLayerIntoRows = (
  devices: V1TopologyDevice[],
  maxColumns: number
): V1TopologyDevice[][] => {
  if (devices.length === 0) return [];
  return Array.from({ length: Math.ceil(devices.length / maxColumns) }, (_, row) =>
    devices.slice(row * maxColumns, (row + 1) * maxColumns)
  );
};

const computeLayeredPositions = (
  devices: V1TopologyDevice[],
  adjacency: Map<string, Set<string>>,
  levels: Map<string, number>,
  horizontalGap: number,
  verticalGap: number
): Map<string, { x: number; y: number }> => {
  const positions = new Map<string, { x: number; y: number }>();
  const byId = new Map(devices.map((device) => [device.id, device]));
  const components = connectedComponents(devices, adjacency);
  const componentGap = horizontalGap * 1.5;
  const populatedRanks = [...new Set(devices.map((device) => levels.get(device.id) ?? 0))].sort((a, b) => a - b);
  const maxColumns = Math.max(8, Math.min(16, Math.ceil(Math.sqrt(Math.max(devices.length, 1) * 2))));
  const orderedByComponent = components.map((ids) => {
    const componentDevices = ids.map((id) => byId.get(id)).filter((device): device is V1TopologyDevice => Boolean(device));
    return orderComponentLayers(componentDevices, adjacency, levels).map((layer) => ({
      rank: layer.rank,
      rows: splitLayerIntoRows(layer.devices, maxColumns)
    }));
  });
  const rowsByRank = new Map<number, number>();
  orderedByComponent.forEach((layers) => {
    layers.forEach((layer) => rowsByRank.set(layer.rank, Math.max(rowsByRank.get(layer.rank) ?? 1, layer.rows.length)));
  });
  const rankStartY = new Map<number, number>();
  let cursorY = 0;
  populatedRanks.forEach((rank) => {
    rankStartY.set(rank, cursorY);
    cursorY += (rowsByRank.get(rank) ?? 1) * verticalGap;
  });
  let cursorX = 0;

  orderedByComponent.forEach((orderedLayers) => {
    const maxCount = Math.max(1, ...orderedLayers.flatMap((layer) => layer.rows.map((row) => row.length)));
    const width = Math.max(horizontalGap, (maxCount - 1) * horizontalGap + horizontalGap);
    const componentCenter = cursorX + width / 2;

    orderedLayers.forEach((layer) => {
      layer.rows.forEach((rowDevices, row) => {
        const start = componentCenter - ((rowDevices.length - 1) * horizontalGap) / 2;
        rowDevices.forEach((device, column) => {
          positions.set(device.id, {
            x: start + column * horizontalGap,
            y: (rankStartY.get(layer.rank) ?? 0) + row * verticalGap
          });
        });
      });
    });
    cursorX += width + componentGap;
  });

  if (positions.size > 0) {
    const xs = [...positions.values()].map((position) => position.x);
    const center = (Math.min(...xs) + Math.max(...xs)) / 2;
    positions.forEach((position, id) => positions.set(id, { x: position.x - center, y: position.y }));
  }
  return positions;
};

function connectivityWalkOrder(devices: V1TopologyDevice[], adjacency: Map<string, Set<string>>): V1TopologyDevice[] {
  const byId = new Map(devices.map((device) => [device.id, device]));
  const unvisited = new Set(byId.keys());
  const ordered: V1TopologyDevice[] = [];
  const degreeWithinLayer = (id: string) =>
    Array.from(adjacency.get(id) ?? []).filter((neighbor) => byId.has(neighbor)).length;
  const ranked = (candidates: string[]) =>
    candidates.sort(
      (a, b) =>
        degreeWithinLayer(b) - degreeWithinLayer(a) ||
        (byId.get(a)?.label ?? a).localeCompare(byId.get(b)?.label ?? b)
    );

  while (unvisited.size > 0) {
    const connectedCandidates = [...unvisited].filter((id) => degreeWithinLayer(id) > 0);
    const start = (connectedCandidates.length > 0 ? connectedCandidates : [...unvisited]).sort(
      (a, b) =>
        degreeWithinLayer(a) - degreeWithinLayer(b) ||
        (byId.get(a)?.label ?? a).localeCompare(byId.get(b)?.label ?? b)
    )[0];
    const visit = (id: string) => {
      if (!unvisited.has(id)) return;
      unvisited.delete(id);
      const device = byId.get(id);
      if (device) ordered.push(device);
      ranked(Array.from(adjacency.get(id) ?? []).filter((neighbor) => unvisited.has(neighbor))).forEach(visit);
    };
    visit(start);
  }
  return ordered;
}

export function computeV1TopologyLayout(
  devices: V1TopologyDevice[],
  adjacency: Map<string, Set<string>>
): V1TopologyLayoutResult {
  const classifications = new Map(devices.map((device) => [device.id, presentationFor(device)]));
  const levels = computeDynamicLevels(devices, adjacency, classifications);
  const sizing = topologySizing(devices.length, edgeCountFor(devices, adjacency));
  const positions = computeLayeredPositions(devices, adjacency, levels, sizing.horizontalGap, sizing.verticalGap);
  return { classifications, levels, positions, ...sizing };
}

export function v1TopologyEdgePresentation(
  sourceId: string,
  targetId: string,
  levels: Map<string, number>,
  positions: Map<string, { x: number; y: number }>,
  index: number
): V1TopologyEdgePresentation {
  const sourceLevel = levels.get(sourceId);
  const targetLevel = levels.get(targetId);
  const direction = index % 2 === 0 ? 1 : -1;
  const routeOffset = direction * (28 + (index % 4) * 12);
  const sourcePosition = positions.get(sourceId);
  const targetPosition = positions.get(targetId);
  const isUnavoidableSameRowPeer =
    sourceLevel === targetLevel &&
    Boolean(sourcePosition && targetPosition) &&
    Math.abs(sourcePosition!.y - targetPosition!.y) < 1;

  return {
    classes: isUnavoidableSameRowPeer
      ? 'topology-edge topology-edge--same-row-peer'
      : 'topology-edge topology-edge--straight',
    routeOffset
  };
}
